// besturimz-website/src/components/FlightsService.js
import React from 'react'
import styled from 'styled-components'
import { motion } from 'framer-motion'
import { FaPlane } from 'react-icons/fa'

const ServiceContainer = styled.div`
  background: white;
  border-radius: 16px;
  padding: 2.5rem;
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.08);
  margin-bottom: 3rem;
  
  @media (max-width: 768px) {
    padding: 1.5rem;
    margin-bottom: 2rem;
  }
`

const Header = styled.div`
  margin-bottom: 2rem;
  text-align: center;
`

const IconWrapper = styled.div`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 80px;
  height: 80px;
  background: ${props => props.domestic ? 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)' : 'linear-gradient(135deg, #48bb78 0%, #38a169 100%)'};
  border-radius: 50%;
  margin-bottom: 1.5rem;
  
  @media (max-width: 768px) {
    width: 60px;
    height: 60px;
  }
`

const Icon = styled.div`
  color: white;
  font-size: 2.5rem;
  
  @media (max-width: 768px) {
    font-size: 2rem;
  }
`

const Title = styled.h2`
  font-size: 2.5rem;
  font-weight: 700;
  color: #2c3e50;
  margin-bottom: 1rem;
  
  @media (max-width: 768px) {
    font-size: 2rem;
  }
`

const Subtitle = styled.p`
  font-size: 1.1rem;
  color: #666;
  line-height: 1.6;
  
  @media (max-width: 768px) {
    font-size: 1rem;
  }
`

const ServicesGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(280px, 1fr));
  gap: 1.5rem;
  margin-bottom: 2rem;
  
  @media (max-width: 768px) {
    grid-template-columns: 1fr;
    gap: 1rem;
  }
`

const ServiceCard = styled(motion.div)`
  background: ${props => props.highlight ? 'linear-gradient(135deg, #fef3c7 0%, #fde68a 100%)' : '#f8f9fa'};
  padding: 1.5rem;
  border-radius: 12px;
  border-left: 4px solid ${props => props.domestic ? '#667eea' : '#48bb78'};
  transition: all 0.3s ease;
  
  &:hover {
    transform: translateY(-5px);
    box-shadow: 0 6px 20px rgba(0, 0, 0, 0.1);
  }
`

const ServiceHeader = styled.div`
  display: flex;
  align-items: center;
  gap: 1rem;
  margin-bottom: 1rem;
`

const ServiceIcon = styled.div`
  color: ${props => props.domestic ? '#667eea' : '#48bb78'};
  font-size: 2rem;
`

const ServiceTitle = styled.div`
  font-size: 1.2rem;
  font-weight: 700;
  color: #2c3e50;
`

const ServiceDescription = styled.p`
  font-size: 0.95rem;
  color: #666;
  line-height: 1.6;
`

const FeaturesSection = styled.div`
  margin-top: 2rem;
`

const FeaturesTitle = styled.h3`
  font-size: 1.5rem;
  font-weight: 700;
  color: #2c3e50;
  margin-bottom: 1.5rem;
  text-align: center;
`

const FeaturesGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
  gap: 1rem;
`

const FeatureItem = styled(motion.div)`
  background: white;
  padding: 1rem;
  border-radius: 10px;
  border: 2px solid ${props => props.domestic ? '#667eea' : '#48bb78'};
  display: flex;
  align-items: center;
  gap: 0.75rem;
  transition: all 0.3s ease;
  
  &:hover {
    background: ${props => props.domestic ? '#f0f4ff' : '#f0fff4'};
    transform: translateX(5px);
  }
`

const FeatureIcon = styled.div`
  color: ${props => props.domestic ? '#667eea' : '#48bb78'};
  font-size: 1.5rem;
`

const FeatureText = styled.div`
  font-size: 0.95rem;
  color: #2c3e50;
  font-weight: 500;
`

const FlightsService = ({ 
  title, 
  subtitle, 
  services = [],
  features = [],
  domestic = true,
  icon = <FaPlane />,
}) => {
  return (
    <ServiceContainer>
      <Header>
        <IconWrapper domestic={domestic}>
          <Icon>{icon}</Icon>
        </IconWrapper>
        <Title>{title}</Title>
        <Subtitle>{subtitle}</Subtitle>
      </Header>

      <ServicesGrid>
        {services.map((service, index) => (
          <ServiceCard
            key={index}
            domestic={domestic}
            highlight={service.highlight}
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5, delay: index * 0.1 }}
            whileHover={{ scale: 1.02 }}
          >
            <ServiceHeader>
              <ServiceIcon domestic={domestic}>{service.icon}</ServiceIcon>
              <ServiceTitle>{service.title}</ServiceTitle>
            </ServiceHeader>
            <ServiceDescription>{service.description}</ServiceDescription>
          </ServiceCard>
        ))}
      </ServicesGrid>

      {features.length > 0 && (
        <FeaturesSection>
          <FeaturesTitle>Rezervasyon Avantajları</FeaturesTitle>
          <FeaturesGrid>
            {features.map((feature, index) => (
              <FeatureItem
                key={index}
                domestic={domestic}
                initial={{ opacity: 0, x: -20 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ duration: 0.5, delay: index * 0.1 }}
                whileHover={{ x: 5 }}
              >
                <FeatureIcon domestic={domestic}>{feature.icon}</FeatureIcon>
                <FeatureText>{feature.text}</FeatureText>
              </FeatureItem>
            ))}
          </FeaturesGrid>
        </FeaturesSection>
      )}
    </ServiceContainer>
  )
}

export default FlightsService

